import { CommonModule, JsonPipe, NgClass, NgIf } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { GlobalService } from '../../services/global.service';
import { RoleService } from '../../services/role.service';
import { DropdownService } from '../../services/dropdown.service';
import { SelectInputComponent } from "../select-input/select-input.component";
import { UserService } from '../../services/user.service';
import { FormsModule } from '@angular/forms';
import { LoginService } from '../../services/login.service';
import { MessengerPanelComponent } from '../messenger-panel/messenger-panel.component';
import { ChatService } from '../../services/chat.service';
import { ExceptionService } from '../../services/exception.service';
import { NotificationService } from '../../services/notification.service';
import { ButtonComponent } from '../button/button.component';

interface BreadcrumbItem { label: string; route?: string; queryParams?: any; }

@Component({
  selector: 'app-header',
  imports: [NgIf, NgClass, SelectInputComponent, CommonModule, FormsModule, JsonPipe, MessengerPanelComponent, ButtonComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
  standalone: true,
})
export class HeaderComponent implements OnDestroy {

  errorMessage: any = false;
  @Input() label: any = ''
  @Input() hide_school_button: boolean = false;
  @Input() sub_label: any = ''
  @Input() show_back: any = true
  show_user_name: any = true;
  params: any = {}

  check_in_modal = false;

  showBackButton: boolean = false;

  breadcrumbs: BreadcrumbItem[] = [];
  breadcrumb_dropdown_open = false;

  private readonly SECTION_ITEMS: Record<string, BreadcrumbItem[]> = {
    'Masters': [
      { label: 'Country', route: '/country/list' },
      { label: 'State', route: '/state/list' },
      { label: 'District', route: '/district/list' },
      { label: 'City', route: '/city/list' },
      { label: 'University', route: '/university/list' },
      { label: 'College', route: '/college/list' },
      { label: 'Level', route: '/level/list' },
      { label: 'Stream', route: '/stream/list' },
      { label: 'Program', route: '/program/list' },
      { label: 'Subject', route: '/subject/list' },
    ],
    'Evaluation Management': [
      { label: 'Academic Year', route: '/academic-year/list' },
      { label: 'Exam', route: '/exam/list' },
      { label: 'Assessment', route: '/assessment/list' },
    ],
    'User Management': [
      { label: 'Roles', route: '/role/list' },
      { label: 'Faculty', route: '/user/list' },
      { label: 'Student', route: '/user/student-list' },
      { label: 'Student Mapping', route: '/student-mapping/list' },
      { label: 'Head Evaluator Mapping', route: '/head-evaluator-mapping/list' },
    ],
    'Script Management': [
      { label: 'Scripts', route: '/add-script/list' },
      { label: 'Barcode Mapping', route: '/barcode-mapping/list' },
    ],
    'Assignments': [
      { label: 'Assign Evaluator', route: '/evaluator-mapping/list', queryParams: { evaluation_type: 'Evaluation' } },
      { label: 'Assign Re-evaluator', route: '/evaluator-mapping/list', queryParams: { evaluation_type: 'Re-Evaluation' } },
    ],

  };

  get breadcrumb_dropdown_items(): BreadcrumbItem[] {
    if (!this.breadcrumbs.length) return [];
    const parent = this.breadcrumbs.length >= 2
      ? this.breadcrumbs[this.breadcrumbs.length - 2].label
      : this.breadcrumbs[0].label;
    return this.SECTION_ITEMS[parent] || [];
  }

  toggle_breadcrumb_dropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.breadcrumb_dropdown_open = !this.breadcrumb_dropdown_open;
  }

  select_breadcrumb_item(item: BreadcrumbItem): void {
    this.breadcrumb_dropdown_open = false;
    if (item.route) {
      this.router.navigate([item.route], item.queryParams ? { queryParams: item.queryParams } : {});
    }
  }

  private readonly BREADCRUMB_MAP: Record<string, BreadcrumbItem[]> = {
    '/dashboard': [{ label: 'Dashboard' }],
    // Masters - Location
    '/country/list': [{ label: 'Masters' }, { label: 'Country' }],
    '/country/form': [{ label: 'Masters' }, { label: 'Country', route: '/country/list' }, { label: 'Add Country' }],
    '/state/list': [{ label: 'Masters' }, { label: 'State' }],
    '/state/form': [{ label: 'Masters' }, { label: 'State', route: '/state/list' }, { label: 'Add State' }],
    '/district/list': [{ label: 'Masters' }, { label: 'District' }],
    '/district/form': [{ label: 'Masters' }, { label: 'District', route: '/district/list' }, { label: 'Add District' }],
    '/city/list': [{ label: 'Masters' }, { label: 'City' }],
    '/city/form': [{ label: 'Masters' }, { label: 'City', route: '/city/list' }, { label: 'Add City' }],
    // Masters - University & College
    '/university/list': [{ label: 'Masters' }, { label: 'University' }],
    '/university/add': [{ label: 'Masters' }, { label: 'University', route: '/university/list' }, { label: 'Add University' }],
    '/university/edit': [{ label: 'Masters' }, { label: 'University', route: '/university/list' }, { label: 'Edit University' }],
    '/college/list': [{ label: 'Masters' }, { label: 'College' }],
    '/college/add': [{ label: 'Masters' }, { label: 'College', route: '/college/list' }, { label: 'Add College' }],
    '/college/edit': [{ label: 'Masters' }, { label: 'College', route: '/college/list' }, { label: 'Edit College' }],
    // Masters - Course
    '/level/list': [{ label: 'Masters' }, { label: 'Level' }],
    '/stream/list': [{ label: 'Masters' }, { label: 'Stream' }],
    '/program/list': [{ label: 'Masters' }, { label: 'Program' }],
    '/subject/list': [{ label: 'Masters' }, { label: 'Subject' }],
    // Evaluation Management
    '/academic-year/list': [{ label: 'Evaluation Management' }, { label: 'Academic Year' }],
    '/exam/list': [{ label: 'Evaluation Management' }, { label: 'Exam' }],
    '/exam/form': [{ label: 'Evaluation Management' }, { label: 'Exam', route: '/exam/list' }, { label: 'Add Exam' }],
    '/assessment/list': [{ label: 'Evaluation Management' }, { label: 'Assessment' }],
    '/assessment/form': [{ label: 'Evaluation Management' }, { label: 'Assessment', route: '/assessment/list' }, { label: 'Add Assessment' }],
    // User Management
    '/role/list': [{ label: 'User Management' }, { label: 'Roles' }],
    '/role-permission/form': [{ label: 'User Management' }, { label: 'Roles', route: '/role/list' }, { label: 'Role Permission' }],
    '/user/list': [{ label: 'User Management' }, { label: 'Faculty' }],
    '/faculty/form': [{ label: 'User Management' }, { label: 'Faculty', route: '/user/list' }, { label: 'Add Faculty' }],
    '/user/student-list': [{ label: 'User Management' }, { label: 'Students' }],
    '/user/student/form': [{ label: 'User Management' }, { label: 'Students', route: '/user/student-list' }, { label: 'Add Students' }],
    '/student-mapping/list': [{ label: 'User Management' }, { label: 'Student Mapping' }],
    '/student-mapping/form': [{ label: 'User Management' }, { label: 'Student Mapping', route: '/student-mapping/list' }, { label: 'Add Student Mapping' }],
    '/head-evaluator-mapping/list': [{ label: 'User Management' }, { label: 'Head Evaluator Mapping' }],
    // Script Management
    '/add-script/list': [{ label: 'Script Management' }, { label: 'Scripts' }],
    '/add-script/form': [{ label: 'Script Management' }, { label: 'Scripts', route: '/add-script/list' }, { label: 'Add Script' }],
    '/barcode-mapping/list': [{ label: 'Script Management' }, { label: 'Barcode Mapping' }],
    // Exam Results
    '/evaluation/script/list': [{ label: 'Evaluations & Re-evaluations' }, { label: 'Scripts' }],
    // Exceptions
    '/exception/list': [{ label: 'Exceptions' }, { label: 'All Exceptions' }],
    '/assigned-exceptions/list': [{ label: 'Exceptions' }, { label: 'Assigned Exceptions' }],
    '/raised-exceptions/list': [{ label: 'Exceptions' }, { label: 'Raised Exceptions' }],
    '/exception/view': [{ label: 'Exceptions', route: '/exception/list' }, { label: 'View Exception' }],
    // Payments (admin)
    '/payment/list': [{ label: 'Payments' }],
    // Logs
    '/activity-log/list': [{ label: 'Activity Logs' }],
    '/face-logs/list': [{ label: 'Face Logs' }],
    // Student Portal
    '/student-portal/list': [{ label: 'Exam Results' }],
    '/transaction-history/list': [{ label: 'Payment History' }],
    '/re-evaluations/list': [{ label: 'Re-Evaluation' }],
    // Settings
    '/settings/project': [{ label: 'Settings' }],
  };

  chat_unread_count: number = 0;
  unread_count: number = 0;
  private unread_poll_ref: any = null;
  private notif_poll_ref: any = null;

  constructor(private router: Router, public gs: GlobalService, public ds: DropdownService, public us: RoleService, private element_ref: ElementRef, public user: UserService, private login: LoginService, private chat: ChatService, public ex: ExceptionService, public notif: NotificationService) {
    this.router.events.subscribe((event) => {
      this.showBackButton = this.router.url.includes('/form'); // Check if the current route contains "/form"
      if (event instanceof NavigationEnd) {
        this.compute_breadcrumbs();
      }
    });
  }
  async ngOnInit() {
    this.compute_breadcrumbs();
    this.load_notifications();
    const saved = localStorage.getItem("selectedRoleId");
    let role_name = localStorage.getItem("selected_role_name");
    this.gs.users.role = role_name
    this.gs.users.id = saved
    if (saved) {
      this.selectedRoleId = +saved;
    }
    await this.ds?.get_dropdown_list(['role']);
    await this.role_api_call();
    this.start_unread_polling();
    this.start_notif_polling();
  }

  private is_chat_role(): boolean {
    const role = this.gs.user_data?.Role?.name;
    return role === 'Evaluator' || role === 'Head Evaluator';
  }

  private is_notif_role(): boolean {
    const role_name = this.gs.user_data?.Role?.name || this.gs.user_data?.role || '';
    const lower = String(role_name).toLowerCase();
    return lower.includes('super') || lower === 'evaluator' || lower === 'head evaluator';
  }

  private start_unread_polling() {
    if (!this.is_chat_role()) return;
    this.fetch_unread_count();
    this.unread_poll_ref = setInterval(() => this.fetch_unread_count(), 10000);
  }

  private async fetch_unread_count() {
    if (!this.is_chat_role()) return;
    try {
      const res = await this.chat.get_unread_count();
      this.chat_unread_count = res?.data?.count ?? 0;
    } catch { }
  }

  private start_notif_polling() {
    if (!this.is_notif_role()) return;
    this.fetch_notif_unread_count();
    this.check_proctor_messages();
    this.notif_poll_ref = setInterval(() => {
      this.fetch_notif_unread_count();
      this.check_proctor_messages();
    }, 10000);
  }

  private async fetch_notif_unread_count() {
    if (!this.is_notif_role()) return;
    try {
      const res = await this.notif.get_unread_count();
      this.unread_count = res?.data?.count ?? 0;
    } catch { }
  }

  private compute_breadcrumbs(): void {
    const path = this.router.url.split('?')[0];
    const qp = this.get_query_params();

    // Query-param-aware special cases
    if (path === '/evaluator-mapping/list') {
      const type = qp['evaluation_type'];
      if (type === 'Re-Evaluation') {
        this.breadcrumbs = [{ label: 'Assignments' }, { label: 'Assign Re-Evaluator' }];
      } else {
        this.breadcrumbs = [{ label: 'Assignments' }, { label: 'Assign Evaluator' }];
      }
      return;
    }
    if (path === '/exam-result/list') {
      const type = qp['result_type'];
      const last = type === 're-evaluation' ? 'Re-evaluation Result' : 'Evaluation Result';
      this.breadcrumbs = [{ label: 'Exam Results' }, { label: last }];
      return;
    }
    // Form pages: label changes based on action query param
    if (path === '/assessment/form') {
      const a = qp['action'];
      const last = a === 'view' ? 'View Assessment' : a === 'edit' ? 'Edit Assessment' : 'Add Assessment';
      this.breadcrumbs = [{ label: 'Evaluation Management' }, { label: 'Assessment', route: '/assessment/list' }, { label: last }];
      return;
    }
    if (path === '/exam/form') {
      const a = qp['action'];
      const last = a === 'view' ? 'View Exam' : a === 'edit' ? 'Edit Exam' : 'Add Exam';
      this.breadcrumbs = [{ label: 'Evaluation Management' }, { label: 'Exam', route: '/exam/list' }, { label: last }];
      return;
    }
    if (path === '/faculty/form') {
      const a = qp['action'];
      const last = a === 'view' ? 'View Faculty' : a === 'edit' ? 'Edit Faculty' : 'Add Faculty';
      this.breadcrumbs = [{ label: 'User Management' }, { label: 'Faculty', route: '/user/list' }, { label: last }];
      return;
    }
    if (path === '/user/student/form') {
      const last = qp['id'] ? 'Edit Students' : 'Add Students';
      this.breadcrumbs = [{ label: 'User Management' }, { label: 'Students', route: '/user/student-list' }, { label: last }];
      return;
    }
    if (path === '/add-script/form') {
      const a = qp['action'];
      const last = a === 'view' ? 'View Script' : a === 'edit' ? 'Edit Script' : 'Add Script';
      this.breadcrumbs = [{ label: 'Script Management' }, { label: 'Scripts', route: '/add-script/list' }, { label: last }];
      return;
    }

    const mapped = this.BREADCRUMB_MAP[path];
    if (mapped) {
      this.breadcrumbs = mapped;
    } else {
      this.breadcrumbs = this.label ? [{ label: this.label }] : [];
    }
  }

  private get_query_params(): Record<string, string> {
    const qs = this.router.url.split('?')[1] || '';
    const params: Record<string, string> = {};
    qs.split('&').forEach(p => {
      const [k, v] = p.split('=');
      if (k) params[decodeURIComponent(k)] = decodeURIComponent(v || '');
    });
    return params;
  }

  navigate_breadcrumb(item: BreadcrumbItem): void {
    if (!item.route) return;
    this.router.navigate([item.route], item.queryParams ? { queryParams: item.queryParams } : {});
  }

  ngOnDestroy() {
    if (this.unread_poll_ref) clearInterval(this.unread_poll_ref);
    if (this.notif_poll_ref) clearInterval(this.notif_poll_ref);
  }
  goBack() {
    if (this.showBackButton) { // Only call goBack() if on the form page
      window.history.back();
    }
  }
  selectedRoleId: any = 4;

  async send_role_in_user_list(event: any) {
    console.log(event);

    this.selectedRoleId = event.value.id;  // ⭐ ngModel update

    // API call
    let response = await this.us.get_list({ id: this.selectedRoleId });

    // store role in localStorage
    localStorage.setItem("selectedRoleId", this.selectedRoleId);
    localStorage.setItem("selected_role_name", response.data[0].name);
    let role_name = localStorage.getItem("selected_role_name");
    this.gs.users.role = role_name

    console.log(response, "response");
  }

  async role_api_call() {
    try {
      const existing_token = this.gs.user_data?.token;
      const existing_role = this.gs.user_data?.role;
      const role_data = await this.user.get_profile();
      if (role_data?.data) {
        // Preserve token and flat role string — get_profile strips token; role may be nested only
        const role_name = role_data.data.Role?.name || existing_role;
        this.gs.user_data = { ...role_data.data, token: role_data.data.token || existing_token, role: role_name };
        localStorage.setItem('app_user', JSON.stringify(this.gs.user_data));
        this.gs.user_role_data = role_data.data.Role;
        localStorage.setItem('user_role', JSON.stringify(this.gs.user_role_data));
      }
    } catch (error) {
      // get_profile failed — fall back to get-user to keep session alive
      try {
        const role_data = await this.user.get(this.gs?.user_data?.id);
        if (role_data?.data) {
          this.gs.user_data = role_data.data;
          localStorage.setItem('app_user', JSON.stringify(this.gs.user_data));
          this.gs.user_role_data = role_data.data.Role;
          localStorage.setItem('user_role', JSON.stringify(this.gs.user_role_data));
        }
      } catch (_) { }
    }
  }

  back() {
    if (!this.show_back) {
      return;
    }
    window.history.back();
  }

  proctor_message_modal_visible = false;
  proctor_message_data: any = null;

  private last_shown_proctor_msg_id: number = 0;
  private pending_proctor_messages: any[] = [];
  private proctor_msg_initialized = false;

  private is_evaluator_role(): boolean {
    const role = this.gs.user_data?.Role?.name;
    return role === 'Evaluator' || role === 'Head Evaluator';
  }

  private async check_proctor_messages(): Promise<void> {
    if (!this.is_evaluator_role()) return;
    try {
      const res = await this.notif.get_list({ sort_by: 'createdAt', sort_order: 'DESC', page_size: 20 });
      const all: any[] = res?.data || [];
      const unread_proctor = all.filter((n: any) =>
        !n.is_read && (n.type === 'proctor_message' || n.entity === 'ProctorMessage')
      );

      if (!this.proctor_msg_initialized) {
        // First check: record current max ID so only truly new messages auto-pop
        this.last_shown_proctor_msg_id = unread_proctor.reduce((max: number, n: any) => Math.max(max, n.id ?? 0), 0);
        this.proctor_msg_initialized = true;
        return;
      }

      const new_msgs = unread_proctor
        .filter((n: any) => (n.id ?? 0) > this.last_shown_proctor_msg_id)
        .sort((a: any, b: any) => a.id - b.id);

      for (const msg of new_msgs) {
        if (!this.pending_proctor_messages.find((p: any) => p.id === msg.id)) {
          this.last_shown_proctor_msg_id = Math.max(this.last_shown_proctor_msg_id, msg.id ?? 0);
          this.pending_proctor_messages.push(msg);
        }
      }

      if (this.pending_proctor_messages.length > 0 && !this.proctor_message_modal_visible) {
        this.show_next_proctor_message();
      }
    } catch { }
  }

  private show_next_proctor_message(): void {
    if (this.pending_proctor_messages.length === 0 || this.proctor_message_modal_visible) return;
    const next = this.pending_proctor_messages.shift();
    this.proctor_message_data = next;
    this.proctor_message_modal_visible = true;
  }

  close_proctor_message_modal(): void {
    this.proctor_message_modal_visible = false;
    this.proctor_message_data = null;
    if (this.pending_proctor_messages.length > 0) {
      setTimeout(() => this.show_next_proctor_message(), 300);
    }
  }

  async acknowledge_proctor_message(): Promise<void> {
    const msg = this.proctor_message_data;
    if (msg?.id) {
      try { await this.notif.mark_as_read([msg.id]); } catch { }
      const idx = this.notifications.findIndex((n: any) => n.id === msg.id);
      if (idx >= 0) this.notifications[idx].is_read = true;
      if (this.unread_count > 0) this.unread_count -= 1;
    }
    this.proctor_message_modal_visible = false;
    this.proctor_message_data = null;
    if (this.pending_proctor_messages.length > 0) {
      setTimeout(() => this.show_next_proctor_message(), 300);
    }
  }

  is_messenger_open: boolean = false;

  toggle_messenger(): void {
    this.is_messenger_open = !this.is_messenger_open;
    if (this.is_messenger_open) {
      this.is_dropdown_visible = false;
      this.chat_unread_count = 0;
    }
  }

  is_dropdown_visible: boolean = false;
  is_notification_visible: boolean = false;
  notifications: any[] = [];
  notification_filter: 'all' | 'unread' | 'read' = 'all';

  get filtered_notifications(): any[] {
    if (this.notification_filter === 'unread') return this.notifications.filter(n => !n.is_read);
    if (this.notification_filter === 'read') return this.notifications.filter(n => n.is_read);
    return this.notifications;
  }

  @HostListener('document:click', ['$event'])
  async handle_outside_click(event: Event): Promise<void> {
    const clicked_inside = this.element_ref.nativeElement.contains(event.target);
    if (!clicked_inside) {
      this.is_dropdown_visible = false;
      this.breadcrumb_dropdown_open = false;
      if (this.is_notification_visible) {
        this.is_notification_visible = false;
        await this.mark_all_notifications_read();
      }
    }
  }

  toggle_menu_state(): void {
    this.is_dropdown_visible = !this.is_dropdown_visible;
    this.is_notification_visible = false;
  }

  async load_notifications(): Promise<void> {
    try {
      const response = await this.notif.get_list({ sort_by: 'createdAt', sort_order: 'DESC', page_size: 100 });
      this.notifications = response?.data || [];
      this.unread_count = this.notifications.filter((n: any) => !n.is_read).length;
    } catch { this.unread_count = 0; }
  }

  async toggle_notifications(): Promise<void> {
    this.is_notification_visible = !this.is_notification_visible;
    this.is_dropdown_visible = false;
    if (this.is_notification_visible) {
      await this.load_notifications();
    }
  }

  async mark_all_notifications_read(): Promise<void> {
    if (!this.unread_count) return;
    try {
      await this.notif.mark_all_read();
    } catch { }
    this.notifications = this.notifications.map((n: any) => ({ ...n, is_read: true }));
    this.unread_count = 0;
  }

  async open_notification(item: any): Promise<void> {
    console.log(item);
    if (!item) return;
    if (!item.is_read && item.id) {
      try { await this.notif.mark_as_read([item.id]); } catch { }
      item.is_read = true;
      if (this.unread_count > 0) this.unread_count -= 1;
    }
    this.is_notification_visible = false;
    if (item.type === 'proctor_message' || item.entity === 'ProctorMessage') {
      this.proctor_message_data = item;
      this.proctor_message_modal_visible = true;
      return;
    }
    if (item.entity === 'Exception') {
      const { university_id, academic_year_id, exam_id, subject_id } = item.meta || {};
      this.router.navigate(['/exception/list'], {
        queryParams: { university_id, academic_year_id, exam_id, subject_id },
      });
      return;
    }
    if (item.entity === 'Re-Evaluation') {
      console.log(item);

      this.router.navigate(['/evaluator-mapping/list'], {
        queryParams: { evaluation_type: 'Re-Evaluation', university_id: item.meta?.university_id, academic_year_id: item.meta?.academic_year_id, exam_id: item.meta?.exam_id, assessment_id: item.meta?.assessment_id },
      });
      return;
    }
    if (item.action_url) {
      this.router.navigateByUrl(item.action_url);
    }
  }

  time_ago(date: string): string {
    if (!date) return '';
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }

  is_uploading_image = false;

  async on_profile_image_change(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    try {
      this.is_uploading_image = true;
      const upload_res = await this.user.upload_image(file);
      const raw_path = upload_res?.data?.url || upload_res?.url || upload_res?.data?.file_path || upload_res?.file_path;
      const image_url = raw_path?.startsWith('http') ? raw_path : `${this.user.hs.base_url}${raw_path}`;
      if (!image_url) { this.gs.toastr_shows_function('Upload failed', 'Error', 'error'); return; }
      await this.user.update_profile({ image: image_url });
      this.gs.user_data = { ...this.gs.user_data, image: image_url };
      localStorage.setItem('app_user', JSON.stringify(this.gs.user_data));
      this.gs.toastr_shows_function('Profile photo updated', 'Success', 'success');
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.message || 'Upload failed', 'Error', 'error');
    } finally {
      this.is_uploading_image = false;
      input.value = '';
    }
  }

  navigate_to_settings() {
    this.is_dropdown_visible = false;
    this.router.navigate(['/settings/project']);
  }

  async logout() {
    try {
      await this.login.logout({ token: this.gs.user_data?.token });
    } catch { }
    localStorage.clear();
    this.router.navigate(['/sign-in']);
  }
}
